import type { TitleFull } from "@anisync/types";
import { ArrowLeftIcon, HardDriveDownloadIcon, SkipForwardIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { episodeDownloadUrl, getEpisodes, getTitle } from "../lib/api";
import { useSession } from "../lib/use-session";
import { useStartEpisode } from "../lib/use-start-episode";
import { useUserPrefs } from "../lib/use-user-prefs";
import { Player } from "../player";
import { type NextEpisode, pickNextEpisode } from "../player/next-episode";

/**
 * Ile sekund odliczamy do automatycznego przejścia.
 *
 * Pięć wystarcza, żeby kliknąć „Zostań tutaj", a nie na tyle, żeby przeszkadzać
 * komuś, kto właśnie tego chciał. Stała, nie liczba w dwóch miejscach: pasek
 * postępu w nakładce rysuje się z tej samej wartości.
 */
const AUTO_NEXT_SECONDS = 5;

/**
 * Ekran odtwarzania. Tytuł pobiera osobno i wyłącznie po to, żeby pokazać nazwę
 * odcinka — odtwarzacz działa bez tej informacji, więc jej brak nie może
 * blokować startu materiału.
 */
export function PlayerScreen() {
  const { mediaFileId = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const startEpisode = useStartEpisode();
  const [title, setTitle] = useState<TitleFull | null>(null);
  const [startingNext, setStartingNext] = useState(false);
  const [nextError, setNextError] = useState<string | null>(null);

  const { session } = useSession();
  const prefs = useUserPrefs();
  const [autoNextIn, setAutoNextIn] = useState<number | null>(null);
  /** Propozycja pokazywana w kadrze po zakończeniu — `null` znaczy „nie pokazuj". */
  const [nextOffer, setNextOffer] = useState<NextEpisode | null>(null);
  const [nextAvailable, setNextAvailable] = useState<NextEpisode | null>(null);

  const titleId = params.get("tytul");
  const episode = params.get("odcinek");
  // Numer odcinka bierzemy z adresu, więc musi przejść walidację — inaczej
  // „?odcinek=abc" trafiłoby do zapisu postępu jako NaN.
  const parsedEpisode = episode !== null ? Number.parseInt(episode, 10) : Number.NaN;
  const episodeNumber = Number.isInteger(parsedEpisode) ? parsedEpisode : null;

  useEffect(() => {
    if (titleId === null) return;
    getTitle(titleId)
      .then(setTitle)
      .catch(() => setTitle(null));
  }, [titleId]);

  /**
   * Dostępność następnego odcinka sprawdzamy **w trakcie oglądania**, nie po końcu.
   *
   * Zapytanie idzie do źródeł (przez cache list odcinków), więc na końcu odcinka
   * kosztowałoby sekundy ciszy w miejscu, w którym nakładka ma się pojawić od razu.
   * Tak samo robią serwisy streamingowe: w chwili napisów końcowych wiedzą już
   * wszystko. Przy okazji odpowiedź mówi, czy materiał leży u nas gotowy.
   *
   * Porażka zapytania nie jest błędem widocznym: brak wiedzy znaczy brak nakładki,
   * a przycisk „Odcinek N" w nagłówku nadal działa jak dotąd.
   */
  useEffect(() => {
    setNextAvailable(null);
    if (titleId === null || episodeNumber === null) return;
    let cancelled = false;
    getEpisodes(titleId)
      .then((episodes) => {
        if (cancelled) return;
        setNextAvailable(pickNextEpisode(episodes, episodeNumber, title?.episodesTotal ?? null));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [titleId, episodeNumber, title?.episodesTotal]);

  const heading =
    title !== null
      ? `${title.titleEnglish ?? title.titleRomaji}${episode !== null ? ` — odcinek ${episode}` : ""}`
      : undefined;

  /**
   * Czy jest sens pokazywać „następny odcinek".
   *
   * Sprawdzamy wobec `episodesTotal` z katalogu, a nie wobec dostępności
   * w źródłach: to drugie wymagałoby odpytania wszystkich providerów przed
   * pokazaniem przycisku, czyli kilku sekund czekania na informację, która
   * przyda się dopiero po kliknięciu. Nieudane pobranie i tak pokaże tabelę prób.
   */
  const nextEpisode = episodeNumber !== null ? episodeNumber + 1 : null;
  const hasNext =
    titleId !== null &&
    nextEpisode !== null &&
    (title === null || title.episodesTotal === null || nextEpisode <= title.episodesTotal);

  const playNext = useCallback(async (): Promise<void> => {
    if (titleId === null || nextEpisode === null) return;
    setStartingNext(true);
    setNextError(null);
    // Ta sama droga co z listy odcinków: powiadomienie potwierdza przejście od razu,
    // bo następny odcinek często trzeba dopiero znaleźć w źródłach. Przejście dodaje
    // NOWY wpis w historii, w odróżnieniu od ponowienia: „wstecz" ma wrócić do
    // poprzedniego odcinka, bo to osobny materiał, a nie ten sam pod nowym id.
    const label = `${title !== null ? (title.titleEnglish ?? title.titleRomaji) : "Odcinek"} — odc. ${nextEpisode}`;
    const ok = await startEpisode({ titleId, episode: nextEpisode, label });
    if (!ok) setNextError("Nie udało się uruchomić następnego odcinka");
    setStartingNext(false);
    // Tożsamość stabilna między renderami, więc odliczanie może mieć to w zależnościach
    // bez restartowania się co render.
  }, [titleId, nextEpisode, title, startEpisode]);

  /**
   * Odliczanie zamiast natychmiastowego przeskoku.
   *
   * Automat bez odliczania zabiera scenę po napisach końcowych i nie daje się
   * zatrzymać inaczej niż cofaniem. Pięć sekund wystarcza, żeby kliknąć „Zostań",
   * a nie na tyle, żeby przeszkadzać komuś, kto właśnie tego chciał.
   */
  useEffect(() => {
    if (autoNextIn === null) return;
    if (autoNextIn <= 0) {
      setAutoNextIn(null);
      void playNext();
      return;
    }
    const timer = setTimeout(() => setAutoNextIn((value) => (value ?? 1) - 1), 1000);
    return () => clearTimeout(timer);
  }, [autoNextIn, playNext]);

  /**
   * Zmiana materiału przerywa odliczanie i zdejmuje nakładkę.
   *
   * Inaczej licznik z poprzedniego odcinka przeskoczyłby widza w środku nowego.
   * Porównanie z refem, a nie samo uruchomienie efektu: zerujemy przy realnej zmianie
   * identyfikatora, nie przy każdym przebiegu. Poprzednia wersja miała tu pustą listę
   * zależności, czyli **nie zerowała nic** poza pierwszym montażem — i to była cicha
   * dziura, bo automat wchodził dopiero po kolejnym zakończeniu.
   */
  const playingRef = useRef(mediaFileId);
  useEffect(() => {
    if (playingRef.current === mediaFileId) return;
    playingRef.current = mediaFileId;
    setAutoNextIn(null);
    setNextOffer(null);
  }, [mediaFileId]);

  const dismissNext = useCallback(() => {
    setAutoNextIn(null);
    setNextOffer(null);
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-xl font-semibold">{heading ?? "Odtwarzanie"}</h1>
        <div className="flex items-center gap-1">
          {titleId !== null && (
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/tytul/${titleId}`}>
                <ArrowLeftIcon />
                Wróć do tytułu
              </Link>
            </Button>
          )}

          {/*
            Pobranie tego, co właśnie leci — tu, bo tu się o tym myśli („to chcę mieć
            u siebie"). Zwykły odnośnik, więc idzie przez menedżer pobierania
            przeglądarki; trasa wymaga konta, dlatego pokazujemy go tylko zalogowanym.
          */}
          {session?.authenticated === true && (
            <Button variant="ghost" size="sm" asChild>
              <a href={episodeDownloadUrl(mediaFileId)} download>
                <HardDriveDownloadIcon />
                Pobierz plik
              </a>
            </Button>
          )}
          {/*
            Dotąd po skończeniu odcinka nie było dokąd pójść: trzeba było wrócić na
            stronę tytułu i odszukać następny numer. Przycisk jest tu, a nie pod
            obrazem, bo pod obrazem zasłaniałby go pasek sterowania.
          */}
          {hasNext && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void playNext()}
              disabled={startingNext}
            >
              {startingNext ? "Zaczynam…" : `Odcinek ${nextEpisode}`}
              <SkipForwardIcon />
            </Button>
          )}
        </div>
      </div>

      {/*
        Propozycja następnego odcinka nie jest już alertem NAD obrazem, a nakładką
        W kadrze — patrz `player/next-episode-overlay.tsx`. Alert nad obrazem znikał
        na pełnym ekranie, czyli w tym jedynym układzie, w którym odcinek najczęściej
        się kończy.
      */}
      {nextError !== null && (
        <Alert variant="destructive">
          <AlertDescription>{nextError}</AlertDescription>
        </Alert>
      )}

      <Player
        mediaFileId={mediaFileId}
        {...(heading !== undefined ? { title: heading } : {})}
        titleId={titleId}
        episodeNumber={episodeNumber}
        trackProgress={session?.authenticated === true}
        nextEpisode={nextOffer}
        nextCountdown={autoNextIn}
        nextCountdownFrom={AUTO_NEXT_SECONDS}
        nextBusy={startingNext}
        coverUrl={title?.coverUrl ?? null}
        onPlayNext={() => void playNext()}
        onDismissNext={dismissNext}
        onEnded={() => {
          /**
           * Nakładka pojawia się tylko wtedy, gdy następny odcinek DA SIĘ wziąć —
           * czyli gdy któreś źródło go ma albo leży już u nas na dysku. Propozycja
           * prowadząca do ekranu błędu byłaby gorsza niż cisza po ostatnim odcinku.
           *
           * Odliczanie zależy od ustawienia konta; bez niego nakładka jest
           * zaproszeniem, a nie automatem zabierającym scenkę po napisach.
           */
          if (nextAvailable === null) return;
          setNextOffer(nextAvailable);
          if (prefs.autoplayNext) setAutoNextIn(AUTO_NEXT_SECONDS);
        }}
        onMediaChange={(next) => {
          // Ponowienie tworzy nowy rekord materiału. Podmieniamy adres bez
          // dokładania wpisu do historii, żeby przycisk „wstecz" nie wracał
          // do identyfikatora, którego już nie ma.
          const query = new URLSearchParams();
          if (titleId !== null) query.set("tytul", titleId);
          if (episode !== null) query.set("odcinek", episode);
          navigate(`/odtwarzacz/${next}?${query.toString()}`, { replace: true });
        }}
      />
    </div>
  );
}
